/**
 * SMS Modal Component
 * Modal for sending SMS messages to patients
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useViewOnly } from '../../contexts';
import './Modal.css';

const SMSModal = ({ isOpen, onClose, language = 'he', preselectedPatient = null }) => {
  const { authFetch } = useAuth();
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [message, setMessage] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const t = {
    he: {
      title: 'שליחת SMS',
      searchPatient: 'חפש מטופל...',
      selectPatient: 'בחר מטופל',
      phone: 'טלפון',
      template: 'תבנית',
      selectTemplate: 'בחר תבנית',
      message: 'תוכן ההודעה',
      charCount: 'תווים',
      send: 'שלח SMS',
      sending: 'שולח...',
      cancel: 'ביטול',
      history: 'היסטוריה',
      noPatients: 'לא נמצאו מטופלים',
      noHistory: 'אין היסטוריית הודעות',
      sentTo: 'נשלח אל',
      sentAt: 'נשלח ב',
      status: 'סטטוס',
      delivered: 'נמסר',
      pending: 'ממתין',
      failed: 'נכשל',
      successMessage: 'ההודעה נשלחה בהצלחה',
      errorMessage: 'שגיאה בשליחת ההודעה',
      requiredFields: 'נא למלא את כל השדות הנדרשים',
      messageTooLong: 'ההודעה ארוכה מדי (מקסימום 480 תווים)',
      placeholders: {
        patient_name: 'שם המטופל',
        clinic_name: 'שם המרפאה',
        date: 'תאריך',
        time: 'שעה'
      }
    },
    en: {
      title: 'Send SMS',
      searchPatient: 'Search patient...',
      selectPatient: 'Select Patient',
      phone: 'Phone',
      template: 'Template',
      selectTemplate: 'Select Template',
      message: 'Message Content',
      charCount: 'characters',
      send: 'Send SMS',
      sending: 'Sending...',
      cancel: 'Cancel',
      history: 'History',
      noPatients: 'No patients found',
      noHistory: 'No message history',
      sentTo: 'Sent to',
      sentAt: 'Sent at',
      status: 'Status',
      delivered: 'Delivered',
      pending: 'Pending',
      failed: 'Failed',
      successMessage: 'Message sent successfully',
      errorMessage: 'Error sending message',
      requiredFields: 'Please fill in all required fields',
      messageTooLong: 'Message too long (max 480 characters)',
      placeholders: {
        patient_name: 'Patient Name',
        clinic_name: 'Clinic Name',
        date: 'Date',
        time: 'Time'
      }
    }
  }[language];

  // Fetch templates on mount
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await authFetch('/api/sms/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      if (preselectedPatient) {
        setSelectedPatient(preselectedPatient);
      }
    }
  }, [isOpen, preselectedPatient, fetchTemplates]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedPatient(preselectedPatient || null);
      setSelectedTemplate('custom');
      setMessage('');
      setError(null);
      setSuccess(null);
      setShowHistory(false);
    }
  }, [isOpen, preselectedPatient]);

  const fetchHistory = async () => {
    try {
      const response = await authFetch('/api/sms/history');
      if (response.ok) {
        const data = await response.json();
        setHistory(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  // Search patients
  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`/api/patients?search=${encodeURIComponent(query)}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.patients || data || []);
      }
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchPatients(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchPatients]);

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const content = language === 'he' ? template.content_he : template.content;
      setMessage(content);
    }
  };

  // Handle patient selection
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchQuery('');
    setSearchResults([]);

    // Replace placeholders in message
    if (message) {
      let updatedMessage = message;
      updatedMessage = updatedMessage.replace(
        /{patient_name}/g,
        patient.first_name + ' ' + patient.last_name
      );
      setMessage(updatedMessage);
    }
  };

  // Send SMS
  const handleSend = async () => {
    // Validation
    if (!selectedPatient || !message.trim()) {
      setError(t.requiredFields);
      return;
    }

    if (message.length > 480) {
      setError(t.messageTooLong);
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch('/api/sms/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: selectedPatient.id,
          phone: selectedPatient.phone,
          patient_name: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
          message: message,
          template_type: selectedTemplate
        })
      });

      if (response.ok) {
        setSuccess(t.successMessage);
        // Reset form after 2 seconds
        setTimeout(() => {
          setSelectedPatient(null);
          setMessage('');
          setSelectedTemplate('custom');
          setSuccess(null);
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || t.errorMessage);
      }
    } catch (err) {
      setError(t.errorMessage);
    } finally {
      setSending(false);
    }
  };

  // Toggle history view
  const toggleHistory = () => {
    if (!showHistory) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = { delivered: t.delivered, pending: t.pending, failed: t.failed };
    return labels[status] || status;
  };

  // Get status class
  const getStatusClass = (status) => {
    const classes = { delivered: 'status-success', pending: 'status-warning', failed: 'status-error' };
    return classes[status] || '';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content modal-medium" dir={language === 'he' ? 'rtl' : 'ltr'}>
        <div className="modal-header">
          <h2>📱 {t.title}</h2>
          <button className="modal-close" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        <div className="modal-body">
          {/* Toggle between send form and history */}
          <div className="sms-tabs">
            <button
              className={`sms-tab ${!showHistory ? 'active' : ''}`}
              onClick={() => setShowHistory(false)}
            >
              {t.title}
            </button>
            <button
              className={`sms-tab ${showHistory ? 'active' : ''}`}
              onClick={toggleHistory}
            >
              {t.history}
            </button>
          </div>

          {!showHistory ? (
            /* Send SMS Form */
            <div className="sms-form">
              {/* Patient Search */}
              <div className="form-group">
                <label>{t.selectPatient}</label>
                {selectedPatient ? (
                  <div className="selected-patient">
                    <div className="patient-info">
                      <span className="patient-name">
                        {selectedPatient.first_name} {selectedPatient.last_name}
                      </span>
                      <span className="patient-phone">
                        {t.phone}: {selectedPatient.phone}
                      </span>
                    </div>
                    <button
                      className="btn-clear"
                      onClick={() => setSelectedPatient(null)}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="patient-search">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t.searchPatient}
                      className="form-input"
                    />
                    {loading && <div className="search-loading">...</div>}
                    {searchResults.length > 0 && (
                      <div className="search-results">
                        {searchResults.map((patient) => (
                          <div
                            key={patient.id}
                            className="search-result-item"
                            onClick={() => handlePatientSelect(patient)}
                          >
                            <span>{patient.first_name} {patient.last_name}</span>
                            <span className="patient-phone-small">{patient.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
                      <div className="no-results">{t.noPatients}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Template Selection */}
              <div className="form-group">
                <label>{t.template}</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="form-select"
                >
                  <option value="">{t.selectTemplate}</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {language === 'he' ? template.name_he : template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Content */}
              <div className="form-group">
                <label>
                  {t.message}
                  <span className={`char-count ${message.length > 480 ? 'over-limit' : ''}`}>
                    {message.length}/480 {t.charCount}
                  </span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="form-textarea"
                  rows={5}
                  maxLength={500}
                />
                {selectedTemplate !== 'custom' && message && (
                  <div className="template-placeholders">
                    <small>
                      {language === 'he' ? 'משתנים: ' : 'Variables: '}
                      {Object.entries(t.placeholders).map(([key, label]) => (
                        <code key={key}>{`{${key}}`}</code>
                      )).reduce((prev, curr) => [prev, ', ', curr])}
                    </small>
                  </div>
                )}
              </div>

              {/* Error/Success Messages */}
              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}
            </div>
          ) : (
            /* SMS History */
            <div className="sms-history">
              {history.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📭</span>
                  <p>{t.noHistory}</p>
                </div>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>{t.sentTo}</th>
                      <th>{t.message}</th>
                      <th>{t.sentAt}</th>
                      <th>{t.status}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="history-patient">
                            {item.patient_name}
                            <small>***{item.phone_last_four}</small>
                          </div>
                        </td>
                        <td className="message-preview">{item.message_preview}</td>
                        <td>{formatDate(item.sent_at)}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(item.status)}`}>
                            {getStatusLabel(item.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t.cancel}
          </button>
          {!showHistory && (
            <button
              className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
              onClick={handleSend}
              disabled={sending || !selectedPatient || !message.trim() || isViewOnly}
              title={isViewOnly ? viewOnlyTip : undefined}
            >
              {sending ? t.sending : t.send}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMSModal;
